import {
  AutoCalculatedItem,
  ManualPreliminaryItem,
  MainService,
  PumpStationData,
  FoulSewersData,
  StormSewersData,
  RoadsData,
} from "../types/tender.types";

// Standard rates for auto-calculated items
export const PRELIMINARY_RATES = {
  cbr: {
    setup: 175,
    test: 60,
  },
  sewer: {
    cctvSurvey: 5, // per meter
    jettingCleaning: 3, // per meter
    mandrelTest: 25, // per 150mm pipe
  },
  road: {
    plateBearingTest: 120, // per test
    coreSample: 85, // per sample
  },
};

// Standard rates for main services by type
export const MAIN_SERVICE_RATES = {
  Water: {
    "25mm": 45,
    "32mm": 52,
    "50mm": 68,
    "63mm": 78,
    "90mm": 95,
    "110mm": 115,
  },
  Gas: {
    "25mm": 42,
    "32mm": 48,
    "63mm": 65,
    "90mm": 85,
    "125mm": 105,
    "180mm": 145,
  },
  Electric: {
    "LV Cable": 35,
    "HV Cable": 85,
    "11kV Cable": 125,
  },
  Telecom: {
    Fibre: 25,
    Copper: 18,
    Ducting: 30,
  },
};

// Pump station standard rates
export const PUMP_STATION_RATES = {
  chamber: {
    Small: 8500,
    Medium: 12500,
    Large: 18500,
  },
  risingMain: {
    "80mm": 45,
    "100mm": 52,
    "150mm": 68,
    "200mm": 85,
    "250mm": 105,
    "300mm": 125,
  },
  pump: {
    Standard: 2500,
    "Heavy Duty": 3500,
    Submersible: 4200,
  },
  controlPanel: {
    Basic: 1500,
    Advanced: 2800,
    Telemetry: 4500,
  },
};

// Default manual preliminary items
export const DEFAULT_MANUAL_ITEMS: Omit<
  ManualPreliminaryItem,
  "id" | "amount"
>[] = [
  {
    description: "Traffic Management",
    quantity: 2.5,
    unit: "sum",
    rate: 0,
    isPercentage: true,
    percentageBase: "total",
  },
  {
    description: "Setting Out",
    quantity: 1,
    unit: "sum",
    rate: 2500,
    isPercentage: false,
  },
  {
    description: "Temporary Works",
    quantity: 1,
    unit: "sum",
    rate: 1800,
    isPercentage: false,
  },
  {
    description: "Site Facilities",
    quantity: 8,
    unit: "week",
    rate: 350,
    isPercentage: false,
  },
  {
    description: "H&S Requirements",
    quantity: 1,
    unit: "sum",
    rate: 1200,
    isPercentage: false,
  },
];

/**
 * Calculate total road length from roads data
 */
export const calculateTotalRoadLength = (roads: RoadsData): number => {
  return roads.cutFillSections.table.reduce((total, section) => {
    // Estimate length from area (assuming average width of 6m)
    return total + section.roadArea / 6;
  }, 0);
};

/**
 * Calculate total road area from roads data
 */
export const calculateTotalRoadArea = (roads: RoadsData): number => {
  return roads.cutFillSections.table.reduce(
    (total, section) => total + section.roadArea,
    0
  );
};

/**
 * Calculate total sewer length from foul and storm sewers
 */
export const calculateTotalSewerLength = (
  foulSewers: FoulSewersData,
  stormSewers: StormSewersData
): number => {
  const foulLength = foulSewers.pipeRuns.table.reduce(
    (total, run) => total + run.length,
    0
  );
  const stormLength = stormSewers.pipeRuns.table.reduce(
    (total, run) => total + run.length,
    0
  );
  return foulLength + stormLength;
};

/**
 * Count 150mm pipes for mandrel testing
 */
export const count150mmPipes = (
  foulSewers: FoulSewersData,
  stormSewers: StormSewersData
): number => {
  const foul150mm = foulSewers.pipeRuns.table.filter(
    (run) => run.diameter === "150mm"
  ).length;
  const storm150mm = stormSewers.pipeRuns.table.filter(
    (run) => run.diameter === "150mm"
  ).length;
  return foul150mm + storm150mm;
};

/**
 * Generate auto-calculated preliminary items
 */
export const generateAutoCalculatedItems = (
  foulSewers: FoulSewersData,
  stormSewers: StormSewersData,
  roads: RoadsData
): AutoCalculatedItem[] => {
  const totalRoadLength = calculateTotalRoadLength(roads);
  const totalRoadArea = calculateTotalRoadArea(roads);
  const totalSewerLength = calculateTotalSewerLength(foulSewers, stormSewers);
  const pipes150mmCount = count150mmPipes(foulSewers, stormSewers);

  const items: AutoCalculatedItem[] = [];

  // CBR Testing
  const cbrVisits = Math.ceil(totalRoadLength / 30 / 4);
  const cbrTests = Math.ceil(totalRoadLength / 30);

  items.push({
    id: "cbr-setup",
    category: "cbr",
    name: "CBR Setup/Mobilization",
    description: "CBR test setup and mobilization",
    quantity: cbrVisits,
    unit: "visits",
    rate: PRELIMINARY_RATES.cbr.setup,
    amount: cbrVisits * PRELIMINARY_RATES.cbr.setup,
    included: true,
    calculationBasis: `ROUNDUP(${totalRoadLength.toFixed(
      3
    )}/30/4) = ${cbrVisits} visits`,
  });

  items.push({
    id: "cbr-tests",
    category: "cbr",
    name: "CBR Tests",
    description: "CBR laboratory tests",
    quantity: cbrTests,
    unit: "Nr",
    rate: PRELIMINARY_RATES.cbr.test,
    amount: cbrTests * PRELIMINARY_RATES.cbr.test,
    included: true,
    calculationBasis: `ROUNDUP(${totalRoadLength.toFixed(
      3
    )}/30) = ${cbrTests} tests`,
  });

  // Sewer Testing
  if (totalSewerLength > 0) {
    items.push({
      id: "cctv-survey",
      category: "sewer",
      name: "CCTV Survey",
      description: "CCTV survey of sewers",
      quantity: totalSewerLength,
      unit: "m",
      rate: PRELIMINARY_RATES.sewer.cctvSurvey,
      amount: totalSewerLength * PRELIMINARY_RATES.sewer.cctvSurvey,
      included: true,
      calculationBasis: `${totalSewerLength.toFixed(2)}m × £${
        PRELIMINARY_RATES.sewer.cctvSurvey
      }/m`,
    });

    items.push({
      id: "jetting-cleaning",
      category: "sewer",
      name: "Jetting & Cleaning",
      description: "High pressure jetting and cleaning",
      quantity: totalSewerLength,
      unit: "m",
      rate: PRELIMINARY_RATES.sewer.jettingCleaning,
      amount: totalSewerLength * PRELIMINARY_RATES.sewer.jettingCleaning,
      included: true,
      calculationBasis: `${totalSewerLength.toFixed(2)}m × £${
        PRELIMINARY_RATES.sewer.jettingCleaning
      }/m`,
    });

    if (pipes150mmCount > 0) {
      items.push({
        id: "mandrel-testing",
        category: "sewer",
        name: "Mandrel Testing",
        description: "Mandrel testing for 150mm pipes",
        quantity: pipes150mmCount,
        unit: "Nr",
        rate: PRELIMINARY_RATES.sewer.mandrelTest,
        amount: pipes150mmCount * PRELIMINARY_RATES.sewer.mandrelTest,
        included: true,
        calculationBasis: `${pipes150mmCount} × 150mm pipes × £${PRELIMINARY_RATES.sewer.mandrelTest}`,
      });
    }
  }

  // Road Testing
  if (totalRoadArea > 0) {
    const plateBearingTests = Math.ceil(totalRoadArea / 500);
    const coreSamples = Math.ceil(totalRoadArea / 1000);

    items.push({
      id: "plate-bearing-tests",
      category: "road",
      name: "Plate Bearing Tests",
      description: "Plate bearing tests for subgrade",
      quantity: plateBearingTests,
      unit: "Nr",
      rate: PRELIMINARY_RATES.road.plateBearingTest,
      amount: plateBearingTests * PRELIMINARY_RATES.road.plateBearingTest,
      included: true,
      calculationBasis: `${totalRoadArea.toFixed(
        3
      )}m² ÷ 500 = ${plateBearingTests} tests`,
    });

    items.push({
      id: "core-samples",
      category: "road",
      name: "Core Samples",
      description: "Core samples for existing pavement",
      quantity: coreSamples,
      unit: "Nr",
      rate: PRELIMINARY_RATES.road.coreSample,
      amount: coreSamples * PRELIMINARY_RATES.road.coreSample,
      included: true,
      calculationBasis: `${totalRoadArea.toFixed(
        3
      )}m² ÷ 1000 = ${coreSamples} samples`,
    });
  }

  return items;
};

/**
 * Calculate manual preliminary items amounts
 */
export const calculateManualItemAmounts = (
  items: ManualPreliminaryItem[],
  projectTotal: number
): ManualPreliminaryItem[] => {
  return items.map((item) => {
    let amount = 0;

    if (item.isPercentage) {
      // Calculate percentage-based amount
      const percentage = item.quantity / 100;
      amount = projectTotal * percentage;
    } else {
      // Calculate standard quantity × rate
      amount = item.quantity * item.rate;
    }

    return {
      ...item,
      amount,
    };
  });
};

/**
 * Calculate main services total
 */
export const calculateMainServicesTotal = (
  mainServices: MainService[]
): number => {
  return mainServices.reduce((total, service) => total + service.amount, 0);
};

/**
 * Calculate pump station total
 */
export const calculatePumpStationTotal = (
  pumpStation: PumpStationData | null
): number => {
  if (!pumpStation) return 0;

  const chamberCost = pumpStation.chamber.rate;
  const risingMainCost =
    pumpStation.risingMain.length * pumpStation.risingMain.rate;
  const pumpCost = pumpStation.pump.quantity * pumpStation.pump.rate;
  const controlPanelCost = pumpStation.controlPanel.rate;

  return chamberCost + risingMainCost + pumpCost + controlPanelCost;
};

/**
 * Validate preliminary items
 */
export const validatePreliminaryItems = (
  items: ManualPreliminaryItem[]
): string[] => {
  const errors: string[] = [];

  items.forEach((item, index) => {
    if (!item.description || item.description.trim() === "") {
      errors.push(`Row ${index + 1}: Description is required`);
    }

    if (item.quantity <= 0) {
      errors.push(`Row ${index + 1}: Quantity must be greater than 0`);
    }

    if (!item.isPercentage && item.rate <= 0) {
      errors.push(`Row ${index + 1}: Rate must be greater than 0`);
    }
  });

  return errors;
};
